import { BarChart3, BookOpen, CalendarDays, ClipboardCheck, ClipboardList, FileUp, GraduationCap, Heart, Languages, LayoutDashboard, Share2, Sigma, Sun } from 'lucide-react';
import { AppSwitcher, Sidebar as OsSidebar, SidebarUser, type NavItem } from '@os/ui';
import { useOsAuth } from '@os/auth-client/react';
import { useMe, useSidebarCounts } from '@/services/api';
import { APP_ID, OS_URL } from '@/app/auth';
import { useUi, type View } from '@/stores/ui.store';
import { useStudent } from '@/hooks/useStudent';
import { bn } from '@/lib/format';
import { mergeAddonNav } from '@/addons/nav';
import { useAddons } from '@/services/api';
import { APP_NAV } from './appNav';
import { BRAND } from './AppShell';

/** Screens per perspective (design §1 audiences). Labels are Bangla, group headers English — as the prototype's sidebar. */
const NAV: Record<View, NavItem[]> = {
  student: [
    { group: 'Overview' },
    { to: '/', label: 'আজকের পড়া', icon: Sun, end: true },
    { to: '/plan', label: 'রুটিন সাজাও', icon: CalendarDays },
    { group: 'Learn' },
    { to: '/math', label: 'গণিত', icon: Sigma, badge: 'homework' },
    { to: '/english', label: 'ইংরেজি', icon: Languages },
    { to: '/library', label: 'লাইব্রেরি', icon: BookOpen },
    { group: 'Work' },
    { to: '/paper', label: 'কাগজ জমা', icon: FileUp, badge: 'paper' },
    { to: '/share', label: 'শেয়ার', icon: Share2 },
    { to: '/exam', label: 'পরীক্ষা প্রস্তুতি', icon: GraduationCap },
    { to: '/feel', label: 'আমার অনুভূতি', icon: Heart },
  ],
  teacher: [
    { group: 'Overview' },
    { to: '/dashboard', label: 'ড্যাশবোর্ড', icon: LayoutDashboard },
    { group: 'Teach' },
    { to: '/review', label: 'খাতা দেখা', icon: ClipboardCheck, badge: 'review' },
    { to: '/homework', label: 'বাড়ির কাজ দিন', icon: ClipboardList },
    { to: '/math', label: 'গণিত', icon: Sigma },
    { to: '/library', label: 'লাইব্রেরি', icon: BookOpen },
    { group: 'Students' },
    { to: '/progress', label: 'অগ্রগতি', icon: BarChart3 },
    { to: '/feel', label: 'অনুভূতি', icon: Heart },
  ],
  parent: [
    { group: 'Overview' },
    { to: '/progress', label: 'অগ্রগতি', icon: BarChart3 },
    { to: '/feel', label: 'অনুভূতির সারাংশ', icon: Heart },
    { to: '/paper', label: 'কাগজ জমা', icon: FileUp },
    { to: '/', label: 'আজকের পড়া', icon: Sun, end: true },
  ],
};
const VIEW_LABEL: Record<View, [string, string]> = { student: ['ছাত্র', 'Student'], teacher: ['শিক্ষক', 'Teacher'], parent: ['অভিভাবক', 'Parent'] };
const ROLE_LABEL: Record<string, string> = { owner: 'Owner', teacher: 'Teacher', student: 'Student', parent: 'Parent' };

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { data } = useMe();
  const { me, student, studentId, view, setView, setStudentId, canPick } = useStudent();
  const counts = useSidebarCounts(studentId);
  const { lang, setLang } = useUi();
  const addons = useAddons();
  const auth = useOsAuth();
  const apps = [
    { id: 'os', name: 'OS', url: OS_URL, icon: 'OS' },
    ...(auth.getClaims()?.apps ?? []).filter((a) => a.id !== 'os' && a.id !== APP_ID).map((a) => ({ id: a.id, name: a.id, url: OS_URL })),
  ];
  const nav = mergeAddonNav([...NAV[view], ...APP_NAV], addons.data?.data ?? []);
  const badges = { review: counts.data?.review, paper: counts.data?.paper, homework: counts.data?.homework };
  return (
    <OsSidebar brand={BRAND} nav={nav} badges={badges} onNavigate={onNavigate} footer={
      <>
        {(me?.views.length ?? 0) > 1 && (
          <div className="ss-side-seg" role="tablist" aria-label="Perspective">
            {me!.views.map((v) => <button key={v} type="button" role="tab" aria-selected={view === v} className={`ss-side-seg-btn${view === v ? ' is-on' : ''}`} onClick={() => setView(v)}><span>{VIEW_LABEL[v][0]}</span><span className="en">{VIEW_LABEL[v][1]}</span></button>)}
          </div>
        )}
        {canPick && view !== 'student' && (
          <label className="ss-side-pick"><span className="en">STUDENT</span>
            <select value={studentId ?? ''} onChange={(e) => setStudentId(e.target.value || null)}>{me!.students.map((s) => <option key={s.id} value={s.id}>{s.name_bn} · {s.classroom_en}</option>)}</select>
          </label>
        )}
        {student && (
          <div className="ss-side-streak"><span className="en">STREAK</span><div><b>{lang === 'bn' ? bn(student.streak_days) : student.streak_days}</b> <span>দিন <span className="en" style={{ display: 'inline' }}>days</span></span></div></div>
        )}
        <div className="ss-side-seg" role="tablist" aria-label="Language">
          <button type="button" role="tab" aria-selected={lang === 'bn'} className={`ss-side-seg-btn${lang === 'bn' ? ' is-on' : ''}`} onClick={() => setLang('bn')}>বাংলা</button>
          <button type="button" role="tab" aria-selected={lang === 'en'} className={`ss-side-seg-btn${lang === 'en' ? ' is-on' : ''}`} onClick={() => setLang('en')}>English</button>
        </div>
        <SidebarUser name={data?.user.display_name ?? '…'} subtitle={(data?.roles ?? []).map((r) => ROLE_LABEL[r] ?? r).join(', ') || 'Signing in'} />
        <AppSwitcher apps={apps} currentAppId={APP_ID} osUrl={OS_URL} logoutHref={`${auth.options.issuer}/logout?post_logout_redirect_uri=${encodeURIComponent(OS_URL)}`} onLogout={() => auth.logout(OS_URL)} />
      </>
    } />
  );
}
